/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Schedule Template Firebase Callable Function Wrappers
 *
 * Thin typed wrappers over the schedule-template callables (served to web +
 * mobile from functions/). Kept separate from bookingQueries so the growing
 * template surface (save, apply, repeat, rename, delete, undo) has its own home.
 */

import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "../store/firebaseApp";
import type {
  ApplyScheduleTemplateRequest,
  ApplyScheduleTemplateResult,
  DuplicateWeekRequest,
  ScheduleTemplateSlot,
  ScheduleTemplateWeekCount,
  UndoScheduleTemplateApplyRequest,
  UndoScheduleTemplateApplyResult,
} from "@lemonadetraining/contracts";

/**
 * Create a new schedule template, or update an existing one when `templateID`
 * is supplied. The server validates every slot (class type belongs to the box,
 * coach is a member, time format, slot cap) and is the authority on name
 * uniqueness.
 *
 * Duplicate name on CREATE (or renaming onto a different existing template)
 * rejects with an `already-exists` HttpsError whose `details.existingTemplateID`
 * is the colliding template's id — the caller uses it to offer "Update existing"
 * (re-submit with that `templateID`) instead of silently duplicating.
 *
 * @param data.boxID - Box that owns the template.
 * @param data.name - Owner-chosen name (server trims + length-caps).
 * @param data.includesCoaches - Mirrors the Save dialog toggle; when false the
 *   template applies coachless regardless of any coach fields on the slots.
 * @param data.weekCount - 1 | 2 | 4 (Phase 1 saves single-week: 1).
 * @param data.slots - Recurrence-free week-shape slots.
 * @param data.templateID - Present = update that template; absent = create.
 * @returns `{ templateID, created }` — created is true on insert, false on update.
 */
export const saveScheduleTemplate = async (data: {
  boxID: string;
  name: string;
  includesCoaches: boolean;
  weekCount: ScheduleTemplateWeekCount;
  slots: ScheduleTemplateSlot[];
  templateID?: string;
}): Promise<{ templateID: string; created: boolean }> => {
  const saveScheduleTemplateFn = httpsCallable(firebaseFunctions, "saveScheduleTemplate");
  const result = await saveScheduleTemplateFn(data);
  return result.data as { templateID: string; created: boolean };
};

// The receipt, request and response shapes are contracts (practice rule 15); the
// names stay exported from here so existing imports keep working.
export type {
  ApplyCoachlessEntry,
  ApplyCreatedEntry,
  ApplyFailedEntry,
  ApplyReceiptWeek,
  ApplyScheduleTemplateRequest,
  ApplyScheduleTemplateResult,
  ApplySkippedEntry,
  ApplySkipReason,
  CoachOverride,
  DuplicateWeekRequest,
  UndoKeptEntry,
  UndoScheduleTemplateApplyCounts,
  UndoScheduleTemplateApplyRequest,
  UndoScheduleTemplateApplyResult,
} from "@lemonadetraining/contracts";

/**
 * Apply a saved schedule template (or preview it with `dryRun: true`, which runs the
 * identical server-side conflict engine but writes nothing and emits no analytics).
 * Applying only ever CREATES new Class docs, so a booked class is never touched.
 *
 * Send exactly one of `targetWeekStartISO` (the whole template from that Monday) or
 * `targetWeekStartISOs` (1 to 8 ascending Mondays, one template week each, rotating
 * A, B, A, B). Build each anchor with `targetWeekStartISOForDate`: it must resolve to
 * the week's MONDAY in the box timezone. Optional keys are OMITTED, never sent as
 * `undefined`, because the callable serializer encodes `undefined` as `null`.
 *
 * `idempotencyToken` is only the 60 s replay key. `applyToken` is the provenance
 * stamped on the created classes: omit it on a first apply, and on a "Finish applying"
 * retry pass the first pass's so one Undo addresses every pass.
 *
 * @returns One receipt and one applyToken for every week the call covered.
 */
export const applyScheduleTemplate = async (
  data: ApplyScheduleTemplateRequest
): Promise<ApplyScheduleTemplateResult> => {
  const payload: ApplyScheduleTemplateRequest = {
    boxID: data.boxID,
    templateID: data.templateID,
  };
  if (data.targetWeekStartISOs && data.targetWeekStartISOs.length > 0) {
    payload.targetWeekStartISOs = data.targetWeekStartISOs;
  } else if (data.targetWeekStartISO) {
    payload.targetWeekStartISO = data.targetWeekStartISO;
  }
  if (typeof data.dryRun === "boolean") payload.dryRun = data.dryRun;
  if (data.idempotencyToken) payload.idempotencyToken = data.idempotencyToken;
  if (data.applyToken) payload.applyToken = data.applyToken;
  // An empty or absent set applies each class with its saved coach.
  if (data.coachOverrides && data.coachOverrides.length > 0) {
    payload.coachOverrides = data.coachOverrides;
  }

  const applyScheduleTemplateFn = httpsCallable(firebaseFunctions, "applyScheduleTemplate");
  const result = await applyScheduleTemplateFn(payload);
  return result.data as ApplyScheduleTemplateResult;
};

/**
 * Repeat a week's classes onto one or more target weeks (or preview with
 * `dryRun: true`) through the same server apply core: no double-create on retry,
 * never touches an existing or booked class. The copies carry the receipt's
 * applyToken, so `undoScheduleTemplateApply({ boxID, applyToken })` removes them.
 *
 * Anchors are box-local MONDAYS (`targetWeekStartISOForDate`). Send at most one of
 * `targetWeekStartISO` / `targetWeekStartISOs`; with neither the server targets the
 * week after the source. `classIDs` copies only that selection.
 *
 * @returns The apply receipt (templateID null, weekCount 1) with
 *   `counts.cancelledNotCopied`.
 */
export const duplicateWeek = async (
  data: DuplicateWeekRequest
): Promise<ApplyScheduleTemplateResult> => {
  const payload: DuplicateWeekRequest = {
    boxID: data.boxID,
    sourceWeekStartISO: data.sourceWeekStartISO,
  };
  if (data.targetWeekStartISOs && data.targetWeekStartISOs.length > 0) {
    payload.targetWeekStartISOs = data.targetWeekStartISOs;
  } else if (data.targetWeekStartISO) {
    payload.targetWeekStartISO = data.targetWeekStartISO;
  }
  if (Array.isArray(data.classIDs)) payload.classIDs = data.classIDs;
  if (typeof data.dryRun === "boolean") payload.dryRun = data.dryRun;
  if (data.idempotencyToken) payload.idempotencyToken = data.idempotencyToken;
  if (data.applyToken) payload.applyToken = data.applyToken;

  const duplicateWeekFn = httpsCallable(firebaseFunctions, "duplicateWeek");
  const result = await duplicateWeekFn(payload);
  return result.data as ApplyScheduleTemplateResult;
};

/**
 * Rename a saved schedule template. ADMIN-ONLY (the server enforces box-admin).
 *
 * The server is the authority on name uniqueness: renaming onto a name another
 * template already holds rejects with an `already-exists` HttpsError. A no-op
 * rename (same name) is accepted. Only the template document's `name` changes —
 * classes already created from it are untouched.
 *
 * @param data.boxID - Box that owns the template.
 * @param data.templateID - Template to rename.
 * @param data.name - New owner-chosen name (server trims + length-caps + HTML-strips).
 * @returns `{ templateID }`.
 */
export const renameScheduleTemplate = async (data: {
  boxID: string;
  templateID: string;
  name: string;
}): Promise<{ templateID: string }> => {
  const renameScheduleTemplateFn = httpsCallable(firebaseFunctions, "renameScheduleTemplate");
  const result = await renameScheduleTemplateFn(data);
  return result.data as { templateID: string };
};

/**
 * Delete a saved schedule template. ADMIN-ONLY (the server enforces box-admin).
 *
 * Deletes ONLY the reusable week-shape document. Classes already created from the
 * template are independent Class docs and are never touched — the product promise
 * the delete-confirm copy makes to the owner ("classes you already created stay").
 *
 * @param data.boxID - Box that owns the template.
 * @param data.templateID - Template to delete.
 * @returns `{ success: true }`.
 */
export const deleteScheduleTemplate = async (data: {
  boxID: string;
  templateID: string;
}): Promise<{ success: boolean }> => {
  const deleteScheduleTemplateFn = httpsCallable(firebaseFunctions, "deleteScheduleTemplate");
  const result = await deleteScheduleTemplateFn(data);
  return result.data as { success: boolean };
};

/**
 * One just-created class the undo KEPT (did not delete) and why. OPEN enum by
 * server contract — a plain `string`, so the server may add reasons without a
 * client change; a consumer MUST render an unrecognised value gracefully. Known
 * members: `has_bookings` (someone booked or waitlisted it in the seconds after
 * apply, so undo left it untouched — the never-destroy-booked guarantee) |
 * `cancelled` | `provenance_mismatch` | `not_your_apply` (a non-admin coach may
 * undo only their OWN applies) | `already_gone`. In the normal path (owner undoing
 * their own just-applied week) only `has_bookings` appears.
 */
export interface UndoKeptEntry {
  classID: string;
  reason: string;
}

/**
 * Per-outcome counts for an undo. `requested` is the deduped candidate count;
 * `undone` == the number deleted; `kept` == kept.length (all reasons); `keptBooked`
 * is the subset kept because a member booked/waitlisted them — the headline number
 * the banner leads with ("N kept because someone booked them").
 */
export interface UndoScheduleTemplateApplyCounts {
  requested: number;
  undone: number;
  kept: number;
  keptBooked: number;
}

/**
 * The structured result of undoing a template apply. `undone` classes were
 * server-re-checked as still-unbooked and hard-deleted; `kept` enumerates every
 * class left in place with its reason (OPEN enum: `has_bookings` | `cancelled` |
 * `provenance_mismatch` | `not_your_apply` | `already_gone`), so the banner
 * announces honestly ("Undid 26 of 29. 3 kept because someone booked them.").
 */
export interface UndoScheduleTemplateApplyResult {
  success: boolean;
  undone: number;
  kept: UndoKeptEntry[];
  counts: UndoScheduleTemplateApplyCounts;
}

/**
 * Undo a template apply or a repeated week: delete the classes it created that are
 * STILL unbooked, and report the ones it kept. The server re-verifies provenance and
 * re-reads bookings and waitlist inside the delete transaction, so a class a member
 * booked after the apply comes back in `kept` with reason `has_bookings`. A coach can
 * undo only their own apply (`not_your_apply`).
 *
 * Omit `createdClassIDs` to undo everything stamped with `applyToken` (the form a
 * multi-week receipt needs); pass it (≤200 ids) to limit the candidates.
 *
 * @returns `{ success, undone, kept: UndoKeptEntry[], counts }`.
 */
export const undoScheduleTemplateApply = async (
  data: UndoScheduleTemplateApplyRequest
): Promise<UndoScheduleTemplateApplyResult> => {
  const payload: UndoScheduleTemplateApplyRequest = {
    boxID: data.boxID,
    applyToken: data.applyToken,
  };
  if (Array.isArray(data.createdClassIDs)) payload.createdClassIDs = data.createdClassIDs;
  const undoScheduleTemplateApplyFn = httpsCallable(firebaseFunctions, "undoScheduleTemplateApply");
  const result = await undoScheduleTemplateApplyFn(payload);
  return result.data as UndoScheduleTemplateApplyResult;
};
